import { addEdge, applyEdgeChanges, applyNodeChanges, Background, MarkerType, ReactFlow, useEdges, useEdgesState, useNodes, useNodesState, useReactFlow } from '@xyflow/react';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import ApplicationNode from './nodecomponents/ApplicationNode';
import { useDispatch, useSelector } from 'react-redux';
import PolicyProperties from './PolicyProperties';
import { clearSelectedNodeRedux, getSelectedNodeRedux, setSelectedNodeRedux } from '../../../../redux/selectedNodeSlice';
import RestfulApiNode from './nodecomponents/RestfulApiNode';
import useAnimatedNodes from '../../../../utils/useAnimatedNodes';
import useExpandCollapse from '../../../../utils/useExpandCollapse';
import ChildNode from './nodecomponents/ChildNode';
import RouterNode from './nodecomponents/RouterNode';
import PathNode from './nodecomponents/PathNode';
import ChildPathNode from './nodecomponents/ChildPathNode';
import DataNode from './nodecomponents/DataNode';
import { setSnackbarMessage } from '../../../../redux/snackbarMessageSlice';
import findChildrenNode from '../../../../utils/findChildrenNode';
import { convertPoliciesStructure } from '../../../../utils/convertPolicyStructure';
import findUsedDRGInPolicy from '../../../../utils/findUsedDRGInPolicy';
import findUsedSubjectAttributesInPolicy from '../../../../utils/findUsedSubjectAttributesInPolicy';
import findUsedClients from '../../../../utils/findUsedClients';
import checkDiagramError from '../../../../utils/checkDiagramError';
import checkDRGValidationFrontend from '../../../../utils/checkDRGValidationFrontend';
import hasFlowChanged from '../../../../utils/hasFlowChanged';
import { gzip } from 'pako';
import { getSubjectSchema } from '../../../../redux/subjectSchemaSlice';
import checkUpdateSubjectSchema from '../../../../utils/checkUpdateSubjectSchema';
import { dataTypes } from '../../../../utils/configDataTypes';
import { createLDAPValidator } from '../../../../utils/ldapTypeValidator';
import { getClients, setClients } from '../../../../redux/clientsSlice';
import DialogReuse from '../../ReuseComponents/DialogReuse';
import { LoadingButton } from '@mui/lab';
import { handleGetSimulatorProfiles } from '../../../../utils/handleGetSimulatorProfiles';
import { getOpenSimulatorPolicy, setOpenDrawerRight, setOpenSimulatorPolicy } from '../../../../redux/layoutControlSlice';
import { setDRGFiles } from '../../../../redux/drgMgtSlice';
import { DIAGRAM_TOOLBAR_SAVE_EVENT, DIAGRAM_TYPES } from '../../../../constants/diagramToolbarEvents';
import { setCurrentPolicy } from '../../../../redux/policyMgtSlice';
import { createPolicyDomainDiagram, toPolicyDomainFile } from '../../../../utils/policyDiagramDomainState';
import { getPolicyFileFromStorage, getPolicyViewportFromStorage, setPolicyFileToStorage, setPolicyViewportToStorage } from '../../../../utils/policyStorageAdapter';
import {
    POLICY_CONNECTION_RULE_REASON,
    evaluatePolicyConnection,
    calculateInheritedEnvironment,
    createPolicyEdge,
    shouldShowPolicyConnectHandle,
} from '../../../../utils/policyConnectionDomainRules';

const treeWidth = 200;
const treeHeight = 100;
const animationDuration = 300;
const edgeMarkerSize = 14;
const edgeStyle = {
    stroke: 'gray',
    strokeWidth: 2
};

const edgeHoverColor = 'gray' || '#1976d2';
const getEdgeColorByState = ({ isSelected, isHovered }) => ((isSelected || isHovered) ? edgeHoverColor : edgeStyle.stroke);
const POLICY_FILE_CHANGED_EVENT = 'policy-file-changed';
const POLICY_FILE_UPDATE_STATUS_KEY = 'policy-file-update-status';
const NO_DIAGRAM_CHANGES_MESSAGE = 'No changes detected in the diagram.';

const applyEdgeSelectionStyle = (edge, isSelected = edge.selected, isHovered = false) => {
    const color = getEdgeColorByState({
        isSelected: Boolean(isSelected),
        isHovered: Boolean(isHovered),
    });

    return {
        ...edge,
        selected: Boolean(isSelected),
        style: {
            ...(edge.style || {}),
            stroke: color,
            strokeWidth: edgeStyle.strokeWidth,
        },
        markerEnd: edge.markerEnd
            ? {
                ...edge.markerEnd,
                color,
                width: edgeMarkerSize,
                height: edgeMarkerSize,
            }
            : edge.markerEnd,
    };
};

const PolicyDiagramNodeTypeContext = React.createContext({
    onTypeChange: () => { },
    isInteractionDisabled: false,
});

function ChildNodeType(props) {
    const { onTypeChange, isInteractionDisabled } = useContext(PolicyDiagramNodeTypeContext);
    return <ChildNode {...props} onTypeChange={onTypeChange} isInteractionDisabled={isInteractionDisabled} />;
}

function ChildPathNodeType(props) {
    const { onTypeChange, isInteractionDisabled } = useContext(PolicyDiagramNodeTypeContext);
    return <ChildPathNode {...props} onTypeChange={onTypeChange} isInteractionDisabled={isInteractionDisabled} />;
}

const nodeTypes = {
    application: ApplicationNode,
    restfulapi: RestfulApiNode,
    data: DataNode,
    child: ChildNodeType,
    childpath: ChildPathNodeType,
    router: RouterNode,
    path: PathNode
};

const getOperatorValues = (operator) => {
    if (!operator) return [];

    if (typeof operator === 'string') {
        return [operator];
    }

    if (typeof operator === 'object') {
        return [operator.label, operator.condition, operator.constraint].filter(Boolean);
    }

    return [];
};

const isOperatorSupported = (operators = [], operator) => {
    const selectedOperatorValues = getOperatorValues(operator);
    if (selectedOperatorValues.length === 0) {
        return false;
    }

    return operators.some((item) => {
        const availableOperatorValues = getOperatorValues(item);
        return availableOperatorValues.some((value) => selectedOperatorValues.includes(value));
    });
};

const isInOperator = (operator) => {
    const operatorValues = getOperatorValues(operator);
    const normalizedValues = operatorValues.map(v => String(v).toUpperCase());
    return normalizedValues.includes('IN') || normalizedValues.includes('NOT IN');
};

function PolicyDiagram(props) {
    const {
        project,
        profile,
        policyFile,
        setPolicyFile,
        nodes,
        setNodes,
        edges,
        setEdges,
        // handleNodesChange,
        // onEdgesChange
    } = props;

    const selectedNode = useSelector(getSelectedNodeRedux);
    const openSimulatorPolicy = useSelector(getOpenSimulatorPolicy);
    const clients = useSelector(getClients);

    const dispatch = useDispatch();

    const {
        zoomTo,
        getViewport,
        getNodes,
        getEdges,
        setViewport,
        fitView,
        screenToFlowPosition,
        getNode,
    } = useReactFlow();

    // const [nodes, setNodes, onNodesChangeState] = useNodesState([]);
    // const [edges, setEdges, onEdgesChangeState] = useEdgesState([]);

    // const [nodes, setNodes] = useState([]);
    // const [edges, setEdges] = useState([]);

    const nodesRef = useRef(nodes);
    const edgesRef = useRef(edges);
    const hasHandledOpenSimulatorRef = useRef(false);
    const handleSaveRef = useRef(null);

    // useEffect(() => {
    //     setStoreNodes(nodes);
    // }, [nodes, setStoreNodes]);

    // useEffect(() => {
    //     setStoreEdges(edges);
    // }, [edges, setStoreEdges]);

    // const [project, setProject] = useState();
    // const [profile, setProfile] = useState();

    const [isUpdatePolicyFile, setIsUpdatePolicyFile] = useState(false);
    const [hoveredEdgeId, setHoveredEdgeId] = useState(null);

    const subjectSchema = useSelector(getSubjectSchema);

    const [openNotifyDialog, setOpenNotifyDialog] = useState(false);
    const [clientsInvalid, setClientsInvalid] = useState([]);

    // useEffect(() => {
    //     const project = JSON.parse(localStorage.getItem('project'));
    //     if (project) {
    //         setProject(project);
    //     }
    //     const profile = JSON.parse(localStorage.getItem('iaam'));
    //     if (profile) {
    //         setProfile(profile);
    //     }
    //     const file = JSON.parse(localStorage.getItem('policy-file'));
    //     if (file) {
    //         console.log("Loaded policy file from localStorage:", file);
    //         setPolicyFile(file);
    //     }
    // }, []);

    useEffect(() => {
        nodesRef.current = nodes;
        edgesRef.current = edges;
    }, [nodes, edges]);

    useEffect(() => {
        const getPolicyUpdated = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/policy-diagram/get-policy/${policyFile.policy_diagram_id}`);
                const result = await response.json();
                const normalizedResult = toPolicyDomainFile(result);
                setPolicyFileToStorage(normalizedResult, { syncLegacy: true });
                dispatch(setCurrentPolicy(normalizedResult));
                setPolicyFile((prevFile) => {
                    if (
                        prevFile?.policy_diagram_id === normalizedResult?.policy_diagram_id
                        && prevFile?.last_updated === normalizedResult?.last_updated
                    ) {
                        return prevFile;
                    }
                    return normalizedResult;
                })
            } finally {
                localStorage.setItem(POLICY_FILE_UPDATE_STATUS_KEY, 'idle');
                window.dispatchEvent(new CustomEvent(POLICY_FILE_CHANGED_EVENT));
            }
        };
        if (isUpdatePolicyFile) {
            getPolicyUpdated();
            setIsUpdatePolicyFile(false);
        }
    }, [isUpdatePolicyFile])

    const handleTypeChange = useCallback((id, newType, positionAbsoluteX, positionAbsoluteY) => {
        const currentNodes = nodesRef.current;
        const currentEdges = edgesRef.current;

        let nodeChanged = {};
        const parentNode = findParentNode(currentEdges, id, currentNodes);
        // console.log("parentNode: ", parentNode);

        let new_subject_constraint = [];
        const subjectConstraint = parentNode?.data?.general_properties?.subjectConstraints;
        if (Array.isArray(subjectConstraint) && subjectConstraint.length > 0) {
            new_subject_constraint = subjectConstraint.map(sub => {
                return {
                    ...sub,
                    inherited: true
                }
            })
        }

        const nextNodes = currentNodes.map((n) => {
            if (n.id === id) {
                const { deletable, ...restNode } = n;
                const { label, ...restData } = n.data;
                if (newType === 'router') {
                    dispatch(setOpenDrawerRight(true));
                    nodeChanged = {
                        ...restNode,
                        type: newType,
                        data: {
                            ...restData,
                            general_properties: {
                                label: '',
                                description: '',
                                // subjectConstraints: [],
                                subjectConstraints: new_subject_constraint
                            },
                            error_text: ["General properties incomplete. Please complete them."],
                            restfulapi_parent_node: parentNode.id,
                            environment_properties: parentNode?.data?.environment_properties || {} // edit latest
                        }
                    }
                } else if (newType === 'path') {
                    dispatch(setOpenDrawerRight(true));
                    if (parentNode.type === 'router') {
                        nodeChanged = {
                            ...restNode,
                            type: newType,
                            data: {
                                ...restData,
                                general_properties: {
                                    label: '',
                                    description: '',
                                    // subjectConstraints: [],
                                    subjectConstraints: new_subject_constraint
                                },
                                error_text: ["General properties incomplete. Please complete them."],
                                restfulapi_parent_node: parentNode.data.restfulapi_parent_node,
                                router_parent_node: parentNode.id
                            }
                        }
                    } else {
                        nodeChanged = {
                            ...restNode,
                            type: newType,
                            data: {
                                ...restData,
                                general_properties: {
                                    label: '',
                                    description: '',
                                    // subjectConstraints: [],
                                    subjectConstraints: new_subject_constraint
                                },
                                error_text: ["General properties incomplete. Please complete them."],
                                restfulapi_parent_node: parentNode.id,
                                environment_properties: parentNode?.data?.environment_properties || {} // edit latest
                            }
                        }
                    }
                }
                return nodeChanged;
            }
            return n;
        });

        const pendingNodes = [];
        const pendingEdges = [];

        if (newType === 'router') {
            const newPathNodeId = uuidv4();
            const newChildNodeId = uuidv4();
            const newPathNode = {
                id: newPathNodeId,
                position: { x: nodeChanged.position.x + 220, y: nodeChanged.position.y - 12 },
                data: {
                    general_properties: {
                        label: '',
                        description: '',
                        // subjectConstraints: [],
                        subjectConstraints: new_subject_constraint,
                        action: null
                    },
                    error_text: ["General properties incomplete. Please complete them."],
                    restfulapi_parent_node: parentNode.id,
                    router_parent_node: id,
                    environment_properties: parentNode?.data?.environment_properties || {} // edit latest
                },
                type: 'path',
                sourcePosition: 'right',
                targetPosition: 'left',
            };
            const newChildPathNode = {
                id: newChildNodeId,
                position: { x: nodeChanged.position.x + 220, y: nodeChanged.position.y + 50 },
                data: {
                    general_properties: {
                        label: '',
                        description: '',
                        // subjectConstraints: [],
                        subjectConstraints: new_subject_constraint,
                    },
                    error_text: [],
                    restfulapi_parent_node: parentNode.id,
                    router_parent_node: id,
                    environment_properties: parentNode?.data?.environment_properties || {} // edit latest
                },
                type: 'childpath',
                sourcePosition: 'right',
                targetPosition: 'left',
                // deletable: false
            };
            pendingNodes.push(newPathNode, newChildPathNode);

            const newPathEdge = {
                id: `${nodeChanged.id}->${newPathNodeId}`,
                style: edgeStyle,
                markerEnd: {
                    type: MarkerType.ArrowClosed,
                    width: edgeMarkerSize,
                    height: edgeMarkerSize,
                    color: edgeStyle.stroke
                },
                source: nodeChanged.id,
                target: newPathNodeId,
            };
            const newChildPathEdge = {
                id: `${nodeChanged.id}->${newChildNodeId}`,
                style: edgeStyle,
                markerEnd: {
                    type: MarkerType.ArrowClosed,
                    width: edgeMarkerSize,
                    height: edgeMarkerSize,
                    color: edgeStyle.stroke
                },
                source: nodeChanged.id,
                target: newChildNodeId,
            };
            pendingEdges.push(newPathEdge, newChildPathEdge);

        }
        const newNodeId = uuidv4();
        // const parentNode = findParentNode(edgesRef.current, id);

        if (!parentNode) {
            console.error("Parent node not found for id:", id);
        } else {
            let newChildNode = {
                id: newNodeId,
                position: { x: positionAbsoluteX, y: positionAbsoluteY + (parentNode.type === 'router' ? 60 : newType === 'path' ? 60 : 35) },
                data: {
                    environment_properties: parentNode?.data?.environment_properties || {} // edit latest
                },
                type: parentNode.type === 'router' ? 'childpath' : 'child',
                sourcePosition: 'right',
                targetPosition: 'left',
                // deletable: false
            };
            if (parentNode.type === 'router') {
                newChildNode.data["restfulapi_parent_node"] = parentNode.data.restfulapi_parent_node
                newChildNode.data["router_parent_node"] = parentNode.id
            } else {
                newChildNode.data["restfulapi_parent_node"] = parentNode.id
            }
            pendingNodes.push(newChildNode);
            const newEdge = {
                id: `${parentNode.id}->${newNodeId}`,
                style: edgeStyle,
                markerEnd: {
                    type: MarkerType.ArrowClosed,
                    width: edgeMarkerSize,
                    height: edgeMarkerSize,
                    color: edgeStyle.stroke
                },
                source: parentNode.id,
                target: newNodeId,
            };
            pendingEdges.push(newEdge);
        }

        const committedNodes = nextNodes.concat(pendingNodes);
        const committedEdges = currentEdges.concat(pendingEdges);

        setNodes(committedNodes);
        setEdges(committedEdges);

        // Keep refs in sync immediately because state/store updates are async.
        nodesRef.current = committedNodes;
        edgesRef.current = committedEdges;

    }, [dispatch, setEdges, setNodes]);

    const findParentNode = (edges, targetNodeId, allNodes = nodesRef.current) => {
        const findTargetEdge = edges.find(edge => edge.target === targetNodeId);
        if (!findTargetEdge) return null;
        const parentNode = allNodes.find(node => node.id === findTargetEdge.source) || getNode(findTargetEdge.source);
        return parentNode;
    };

    const { nodes: visibleNodes, edges: visibleEdges } = useExpandCollapse(
        nodes,
        edges,
        { treeWidth, treeHeight }
    );
    const { nodes: animatedNodes } = useAnimatedNodes(visibleNodes, {
        animationDuration,
    });

    const onNodesChange = useCallback(
        async (changes) => {
            if (openSimulatorPolicy) {
                return;
            }

            setNodes((nodes) => {
                const oldNodes = nodes.map(
                    (node) => ({
                        ...node,
                        measured: {
                            width: node?.measured?.width || 150,
                            height: node?.measured?.height || 40,
                        }
                    })
                );
                return applyNodeChanges(changes, oldNodes);
            });
        },
        [openSimulatorPolicy, setNodes],
    );

    const onEdgesChange = useCallback(
        (changes) => {
            if (openSimulatorPolicy) {
                return;
            }

            setEdges((eds) => {
                const updatedEdges = applyEdgeChanges(changes, eds);
                return updatedEdges.map((edge) => applyEdgeSelectionStyle(edge, edge.selected, edge.id === hoveredEdgeId));
            });
        },
        [hoveredEdgeId, openSimulatorPolicy, setEdges],
    );

    // Memoized: Get list of clients (fetch only once)
    const getListClients = useCallback(async () => {
        try {
            const response = await fetch(`https://${process.env.REACT_APP_OP_HOST}/iap-api/get-clients?app_type=WEB,NATIVE,API`);
            const data = await response.json();
            dispatch(setClients(data));
            return data;
        } catch (error) {
            console.error('❌ Error fetching clients:', error);
            return [];
        }
    }, [dispatch]);

    // Memoized: Get DRG list for specific project
    const getDRGList = useCallback(async () => {

        if (!project?.project_id) {
            return;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/regulatory-diagram/get-regulatories/${project.project_id}`);
            const data = await response.json();
            const newData = data.map((item) => ({
                id: item.regulatory_diagram_id,
                name: item.regulatory_diagram_name,
                validation: item.validation,
                action: item?.regulatory_diagram_action || null
            }));
            dispatch(setDRGFiles(newData));
        } catch (error) {
            console.error('❌ Error fetching DRG list:', error);
        }
    }, [dispatch, project?.project_id]);

    const filterClientsInvalid = (nodes, clients) => {
        const usedClients = findUsedClients(nodes);
        const invalidClients = usedClients.filter(used => {
            return !clients.some(client => client.client_name === used.client_name && client.app_type === used.app_type);
        });
        // console.log('invalidClients', invalidClients);
        setClientsInvalid(invalidClients);
        if (invalidClients.length > 0) {
            setOpenNotifyDialog(true);
        }
    };

    const checkSubjectConstraintValidation = (nodes, currentSubjectSchema = subjectSchema) => {
        const resolvedSubjectSchema = Array.isArray(currentSubjectSchema) ? currentSubjectSchema : [];

        const validationNodes = nodes.map(node => {
            if (node.data?.general_properties?.subjectConstraints) {
                let new_error_text = node?.data?.error_text.filter(err => (err !== 'Invalid constraint.' && err !== 'Constraint invalid. Please correct it.'));

                if (node.data?.general_properties?.subjectConstraints.length > 0) {
                    const checkSubjectConstraintError = node.data.general_properties.subjectConstraints.map(constraint => {
                        const checkSubConstraintError = constraint.child.map(sub => {
                            const { attr_error_text, data_type_error_text, operator_error_text, ...sub_without_error } = sub;
                            const hasAttribute = resolvedSubjectSchema.some(attr => attr.name === sub.l_operand.name);
                            if (!hasAttribute) {
                                return {
                                    ...sub_without_error,
                                    attr_error_text: "Attribute no longer available. Please select another."
                                }
                            } else {
                                // ตรวจสอบ attribute type ของ attribute ที่เลือกใน suject constraint กับ subject schema ใน authorized data ว่าตรงกันหรือไม่
                                const newDataType = resolvedSubjectSchema.find(attr => attr.name === sub.l_operand.name)?.attributeType;
                                // const new_group_type = dataTypes.find(dt => dt.similar_type.includes(newDataType));
                                // const new_operators = new_group_type ? new_group_type.operators : [];

                                let subjectAttr = {
                                    ...sub_without_error,
                                    l_operand: {
                                        ...sub_without_error.l_operand,
                                        // attributeType: newDataType,
                                        // operators: new_operators,
                                        // type: new_group_type ? new_group_type.type : undefined
                                    }
                                }

                                if (sub.l_operand.type !== newDataType) {
                                    subjectAttr = {
                                        ...subjectAttr,
                                        attr_error_text: `Attribute type changed. Expected ${newDataType}.`
                                    }
                                }

                                // const typeValidator = createLDAPValidator(newDataType);

                                // const normalizedNewDataType = String(newDataType).toLowerCase();
                                // const normalizedOperator = String(sub_without_error.operator).toLowerCase();
                                // const isArrayIncludesOperator = (normalizedNewDataType === 'array' || normalizedNewDataType.includes('array'))
                                //     && (normalizedOperator === 'includes' || normalizedOperator.includes('includes'));
                                // const isArrayTypeWithStringValue = (normalizedNewDataType === 'array' || normalizedNewDataType.includes('array'))
                                //     && !Array.isArray(sub_without_error.r_operand)
                                //     && sub_without_error.r_operand !== '';

                                // // Check for IN/NOT IN operators
                                // const isInNotInOp = isInOperator(sub_without_error.operator);
                                // const isArrayValue = Array.isArray(sub_without_error.r_operand);

                                // let validateResult;

                                // if (isArrayIncludesOperator && isArrayTypeWithStringValue) {
                                //     // For array type with array value, accept each element without strict array validation
                                //     validateResult = {
                                //         valid: true,
                                //         parsed: sub_without_error.r_operand,
                                //         error: null
                                //     };
                                // } else if (isInNotInOp && isArrayValue) {
                                //     // For IN/NOT IN operators with array value, accept without strict validation
                                //     validateResult = {
                                //         valid: true,
                                //         parsed: sub_without_error.r_operand,
                                //         error: null
                                //     };
                                // } else {
                                //     validateResult = typeValidator.validate(sub_without_error.r_operand);
                                // }

                                // const isOperatorMismatch = !isInNotInOp && !isArrayIncludesOperator && !isOperatorSupported(subjectAttr.l_operand.operators, sub.operator);

                                // const isOperatorMismatch = !isInNotInOp && !isArrayIncludesOperator && !isOperatorSupported(new_operators, sub.operator);

                                // if (isOperatorMismatch) {
                                //     subjectAttr = {
                                //         ...subjectAttr,
                                //         operator_error_text: "Operator for attribute type invalid."
                                //     }
                                // }

                                // if (!validateResult.valid && (isInNotInOp || isArrayIncludesOperator)) {
                                //     subjectAttr = {
                                //         ...subjectAttr,
                                //         r_operand: sub_without_error.r_operand
                                //     }
                                // } else if (!validateResult.valid) {
                                //     subjectAttr = {
                                //         ...subjectAttr,
                                //         data_type_error_text: validateResult.error
                                //     }
                                // } else {
                                //     subjectAttr = {
                                //         ...subjectAttr,
                                //         r_operand: validateResult.parsed
                                //     }
                                // }

                                return subjectAttr;
                            }
                        });

                        const hasSubError = checkSubConstraintError.some(sub => sub.attr_error_text || sub.data_type_error_text || sub.operator_error_text);
                        if (hasSubError) {
                            return {
                                ...constraint,
                                child: checkSubConstraintError,
                                error: true,
                            }
                        } else {
                            const { error, ...constraint_without_error } = constraint;
                            return {
                                ...constraint_without_error,
                                child: checkSubConstraintError
                            };
                        }

                    });

                    const hasError = checkSubjectConstraintError.some(constraint => constraint.error === true);

                    if (hasError) {
                        new_error_text.push("Constraint invalid. Please correct it.");
                    }

                    return {
                        ...node,
                        data: {
                            ...node.data,
                            general_properties: {
                                ...node.data.general_properties,
                                subjectConstraints: checkSubjectConstraintError
                            },
                            error: new_error_text.length > 0,
                            error_text: new_error_text
                        }
                    }

                }

                return {
                    ...node,
                    data: {
                        ...node.data,
                        error: new_error_text.length > 0,
                        error_text: new_error_text
                    }
                };
            }

            return node;

        });

        return validationNodes;
    };

    const onDrop = useCallback(
        (event) => {
            event.preventDefault();

            if (openSimulatorPolicy) {
                return;
            }

            const type = event.dataTransfer.getData('application/reactflow');

            // check if the dropped element is valid
            if (typeof type === 'undefined' || !type) {
                return;
            }

            const position = screenToFlowPosition({
                x: event.clientX,
                y: event.clientY,
            });

            let initialChildNode = false;

            let newNode = {
                id: uuidv4(),
                type: type,
                position,
                data: {
                    general_properties: {
                        name: null,
                        description: '',
                        subjectConstraints: [],
                        action: '',
                    },
                    environment_properties: {
                        resources: [],
                        ip_address_verification: {
                            ipv4: {
                                wild_card: [],
                                subnet_cidr: [],
                                range: [],
                                active: false
                            }
                        },
                        date_time_verification: {
                            active: false,
                            date_time: []
                        }
                    },
                    error_text: ["General properties incomplete. Please complete them."],
                },
            };

            if (type === 'application') {
                newNode.data.general_properties.action = 'OPEN';
            }

            if (type === 'restfulapi') {
                newNode.data.general_properties.action = ['GET', 'POST', 'PUT', 'DELETE'];
                newNode.data.expanded = false;
                newNode.data.expandable = true;
                newNode.data.general_properties['prefix_path'] = '';
                initialChildNode = true;
            }

            if (type === 'data') {
                // const { name, action, ...currentProperties } = newNode.data.general_properties;
                const { name, ...currentProperties } = newNode.data.general_properties;
                newNode = {
                    ...newNode,
                    data: {
                        ...newNode.data,
                        general_properties: {
                            ...currentProperties,
                            drg: null
                        },
                        // error_text: ["General properties incomplete. Please complete them.", "No RESTful API node connection. Please connect this node to a RESTful API node."]
                        error_text: ["General properties incomplete. Please complete them."]
                    }
                }
            }

            const pendingNodes = [newNode];
            const pendingEdges = [];

            if (initialChildNode) {
                const newChildNodeId = uuidv4();

                const positionAbsoluteX = newNode.position.x;
                const positionAbsoluteY = newNode.position.y;

                pendingNodes.push({
                    id: newChildNodeId,
                    position: { x: positionAbsoluteX + 220, y: positionAbsoluteY + 12 },
                    data: {
                        restfulapi_parent_node: newNode.id
                    },
                    type: 'child',
                    sourcePosition: 'right',
                    targetPosition: 'left',
                });

                const newEdge = {
                    id: `${newNode.id}->${newChildNodeId}`,
                    style: edgeStyle,
                    markerEnd: {
                        type: MarkerType.ArrowClosed,
                        width: edgeMarkerSize,
                        height: edgeMarkerSize,
                        color: edgeStyle.stroke
                    },
                    source: newNode.id,
                    target: newChildNodeId,
                };

                pendingEdges.push(newEdge);
            }

            const currentNodes = nodesRef.current;
            const currentEdges = edgesRef.current;
            const committedNodes = currentNodes.concat(pendingNodes);
            const committedEdges = currentEdges.concat(pendingEdges);

            setNodes(committedNodes);
            setEdges(committedEdges);

            // Keep refs in sync immediately because state/store updates are async.
            nodesRef.current = committedNodes;
            edgesRef.current = committedEdges;

        },
        [openSimulatorPolicy, screenToFlowPosition, setEdges, setNodes]
    );

    const onDragOver = useCallback((event) => {
        event.preventDefault();
        if (openSimulatorPolicy) {
            event.dataTransfer.dropEffect = 'none';
            return;
        }
        event.dataTransfer.dropEffect = 'move';
    }, [openSimulatorPolicy]);

    const saveViewportState = useCallback(() => {
        const viewport = getViewport();
        const fallbackPolicyFile = getPolicyFileFromStorage({
            normalize: toPolicyDomainFile,
        });
        const currentPolicyId = policyFile?.policy_diagram_id || fallbackPolicyFile?.policy_diagram_id;

        if (!currentPolicyId) {
            return;
        }

        setPolicyViewportToStorage(
            { viewport },
            {
                policyId: currentPolicyId,
                syncLegacy: true,
            }
        );
    }, [getViewport, policyFile?.policy_diagram_id]);

    // const onNodeDragStart = useCallback((e, node, nodes) => {
    //     // console.log("onNodeDragStart: ", node)
    //     // setNodes((prev) =>
    //     //     prev.map((nd) => ({ ...nd, data: { ...nd.data, showHandle: false } }))
    //     // )
    //     // dispatch(setSelectedNodeRedux(node));
    //     // setFirstCreate(false);
    //     // setOpenProperties(true);
    // }, []);

    const onNodeDoubleClick = useCallback(() => {
        // console.log("onNodeDoubleClick");
        if (openSimulatorPolicy) {
            return;
        }
        dispatch(setOpenDrawerRight(true));
    }, [dispatch, openSimulatorPolicy]);

    const onNodeClick = (event, nodeId) => {
        if (event?.button !== 0) {
            return;
        }

        const node = getNode(nodeId.id);
        // console.log("click node: ", node);

        event.stopPropagation(); // ป้องกัน event ซ้อนกัน
        if (!node) return;

        if (openSimulatorPolicy) {
            event.preventDefault();
            dispatch(setSelectedNodeRedux({ ...node, selected: true }));
            dispatch(setOpenDrawerRight(true));
            return;
        }

        dispatch(setSelectedNodeRedux(node));

        // setNodes((prev) =>
        //     prev.map((nd) => ({
        //         ...nd,
        //         data: {
        //             ...nd.data,
        //             showHandle: (nd.id === nodeId.id) ? true : false,
        //         },
        //     }))
        // )

        setNodes((nds) =>
            nds.map((n) => {
                if (n.id === node.id) {
                    return {
                        ...n,
                        data: {
                            ...n.data,
                            expanded: !n?.data?.expanded,
                            showHandle: true
                        },
                    };
                }

                return {
                    ...n,
                    data: {
                        ...n.data,
                        showHandle: false
                    }
                };
            })
        );

    };

    const onPaneClick = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        if (openSimulatorPolicy) {
            return;
        }
        setNodes((prev) =>
            prev.map((node) => ({ ...node, data: { ...node.data, showHandle: false } }))
        )
        setEdges((prev) => prev.map((edge) => applyEdgeSelectionStyle(edge, false)));
        dispatch(clearSelectedNodeRedux());
    }, [dispatch, setEdges, setNodes, openSimulatorPolicy]);

    const onEdgeClick = useCallback((event, edge) => {
        // console.log("click edge: ", edge);
        if (openSimulatorPolicy) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        setEdges((prev) => prev.map((item) => applyEdgeSelectionStyle(item, item.id === edge.id)));
    }, [openSimulatorPolicy, setEdges]);

    const onEdgeMouseEnter = useCallback((event, edge) => {
        if (openSimulatorPolicy) {
            return;
        }
        setHoveredEdgeId(edge.id);
        setEdges((prev) => prev.map((item) => applyEdgeSelectionStyle(item, item.selected, item.id === edge.id)));
    }, [openSimulatorPolicy, setEdges]);

    const onEdgeMouseLeave = useCallback(() => {
        if (openSimulatorPolicy) {
            return;
        }
        setHoveredEdgeId(null);
        setEdges((prev) => prev.map((item) => applyEdgeSelectionStyle(item, item.selected, false)));
    }, [openSimulatorPolicy, setEdges]);

    useEffect(() => {
        if (!nodes || !selectedNode) return;

        const targetNode = nodes.find(node => node.id === selectedNode.id);
        if (!targetNode) return;

        // เปรียบเทียบเฉพาะ position และ data ที่สำคัญ
        const positionChanged =
            targetNode.position?.x !== selectedNode.position?.x ||
            targetNode.position?.y !== selectedNode.position?.y;

        // เปรียบเทียบ data ด้วย JSON string (ใช้เมื่อจำเป็นจริง ๆ)
        let dataChanged = false;
        try {
            dataChanged = JSON.stringify(targetNode.data) !== JSON.stringify(selectedNode.data);
        } catch (e) {
            // ถ้า data มี circular reference ให้ข้ามการเปรียบเทียบ
            dataChanged = false;
        }

        if (positionChanged || dataChanged) {
            const updated = {
                id: targetNode.id,
                position: { ...targetNode.position },
                data: { ...targetNode.data },
                type: targetNode.type
            };
            dispatch(setSelectedNodeRedux(updated));
        }

    }, [nodes, dispatch]);

    const onConnectStart = (event, params) => {
        if (openSimulatorPolicy) {
            return;
        }
        // console.log("onConnectStart: ", params);
        const start_node = getNode(params.nodeId);
        setNodes(nodes => nodes.map((node) => ({
            ...node,
            data: { ...node.data, showHandle: shouldShowPolicyConnectHandle(start_node, node) }
        })));
    };

    const onConnect = useCallback(
        async (params) => {
            if (openSimulatorPolicy) {
                return;
            }

            const sourceNode = getNode(params.source);
            const targetNode = getNode(params.target);

            const connectionValidation = evaluatePolicyConnection({
                params,
                sourceNode,
                targetNode,
                edges,
            });
            if (!connectionValidation.valid) {
                if (connectionValidation.reason === POLICY_CONNECTION_RULE_REASON.ACTION_MISMATCH) {
                    return dispatch(setSnackbarMessage({
                        open: true,
                        message: `Error: Action mismatch. Please correct the action in general properties.`,
                        status: "error",
                    }));
                }
                return;
            }

            const newEdge = createPolicyEdge({
                params,
                sourceNode,
                targetNode,
                edgeColor: edgeStyle.stroke,
            });

            const newEdges = addEdge(applyEdgeSelectionStyle(newEdge, false), edges);

            setEdges(newEdges);

            handleValidateInherited(sourceNode, targetNode);

        },
        [openSimulatorPolicy, setEdges, edges, getNode, dispatch],
    );

    const updateEnvironmentToChilds = (newEnvProp, targetNode) => {
        if (targetNode?.type === 'restfulapi') {
            const nodes_update = getListChildsUpdateEnvironment(targetNode);
            setNodes((prev) => prev.map((node) => {
                if (nodes_update.some(n => n === node.id)) {
                    return {
                        ...node,
                        data: {
                            ...node.data,
                            environment_properties: newEnvProp
                        }
                    }
                }
                return node;
            }))
        }
    };

    const getListChildsUpdateEnvironment = (targetNode) => {
        let list_nodes = [];
        const traverseChilds = (nodeId) => {
            const childs = findChildrenNode(nodesRef.current, edgesRef.current, nodeId);
            for (const nodeId of childs) {
                let node = getNode(nodeId);
                if (node?.type === 'router' || node?.type === 'path') {
                    list_nodes.push(node.id)
                    traverseChilds(node.id)
                } else {
                    continue;
                }
            }
        };
        if (targetNode?.id) {
            traverseChilds(targetNode.id)
        }
        return list_nodes;
    };

    const handleValidateInherited = (sourceNode, targetNode) => {
        const apiParentNode = sourceNode?.type === 'path'
            ? getNode(sourceNode?.data?.restfulapi_parent_node)
            : null;

        const inheritedResult = calculateInheritedEnvironment({
            sourceNode,
            targetNode,
            apiParentNode,
        });

        if (!inheritedResult) {
            return;
        }

        if (inheritedResult.type === 'application-to-restfulapi') {
            setNodes((prev) =>
                prev.map((node) => {
                    if (node.id === targetNode.id) {
                        return inheritedResult.targetNode;
                    }
                    return node;
                })
            );
            updateEnvironmentToChilds(inheritedResult.inheritedResources, targetNode);
            return;
        }

        if (inheritedResult.type === 'path-to-data') {
            setNodes((prev) =>
                prev.map((node) => (node.id === targetNode.id ? inheritedResult.targetNode : node))
            );
        }
    };

    const PROTECTED_EDGE_TARGET_TYPES = new Set(['router', 'path', 'child', 'childpath']);
    const INHERITED_ENV_NODE_TYPES = new Set(['router', 'path', 'child', 'childpath']);

    const appendToSetIndex = (indexMap, key, value) => {
        if (!key) {
            return;
        }
        if (!indexMap.has(key)) {
            indexMap.set(key, new Set());
        }
        indexMap.get(key).add(value);
    };

    const createDeletionIndexes = (allNodes) => {
        const nodeById = new Map();
        const nodeIdsByRouterParentId = new Map();
        const nodeIdsByRestfulApiParentId = new Map();

        allNodes.forEach((node) => {
            nodeById.set(node.id, node);
            appendToSetIndex(nodeIdsByRouterParentId, node?.data?.router_parent_node, node.id);
            appendToSetIndex(nodeIdsByRestfulApiParentId, node?.data?.restfulapi_parent_node, node.id);
        });

        return {
            nodeById,
            nodeIdsByRouterParentId,
            nodeIdsByRestfulApiParentId,
        };
    };

    const calculateDeletionClosure = ({ deletedNodes, allNodes }) => {
        const deletedNodeIdSet = new Set(deletedNodes.map((node) => node.id));
        const nodeIdsToRemove = new Set(deletedNodeIdSet);
        const { nodeIdsByRouterParentId, nodeIdsByRestfulApiParentId } = createDeletionIndexes(allNodes);

        for (const node of deletedNodes) {
            if (node.type === 'child') {
                const hasRestfulApiParent = deletedNodeIdSet.has(node?.data?.restfulapi_parent_node);
                if (!hasRestfulApiParent) {
                    nodeIdsToRemove.delete(node.id); // ไม่ลบ child node ที่ parent ยังไม่ถูกลบ
                }
                continue;
            }

            if (node.type === 'childpath') {
                const hasRouterParent = deletedNodeIdSet.has(node?.data?.router_parent_node);
                if (!hasRouterParent) {
                    nodeIdsToRemove.delete(node.id); // ไม่ลบ childpath node ที่ parent ยังไม่ถูกลบ
                }
                continue;
            }

            if (node.type === 'router') {
                const routerChildren = nodeIdsByRouterParentId.get(node.id) || new Set();
                routerChildren.forEach((childId) => nodeIdsToRemove.add(childId));
                continue;
            }

            if (node.type === 'restfulapi') {
                const apiChildren = nodeIdsByRestfulApiParentId.get(node.id) || new Set();
                apiChildren.forEach((childId) => nodeIdsToRemove.add(childId));
            }
        }

        return nodeIdsToRemove;
    };

    const calculateEdgesToRemove = ({ deletedEdges, allNodes }) => {
        const edgeIdsToRemove = new Set();
        const { nodeById } = createDeletionIndexes(allNodes);

        for (const edge of deletedEdges) {
            const sourceNode = nodeById.get(edge.source);
            const targetNode = nodeById.get(edge.target);
            const isProtectedEdge = Boolean(sourceNode) && PROTECTED_EDGE_TARGET_TYPES.has(targetNode?.type);

            if (!isProtectedEdge) {
                edgeIdsToRemove.add(edge.id);
            }
        }

        return edgeIdsToRemove;
    };

    const clearInheritedProperties = ({ allNodes, allEdges, edgeIdsToRemove }) => {
        const edgesToRemove = allEdges.filter((edge) => edgeIdsToRemove.has(edge.id));
        if (edgesToRemove.length === 0) {
            return allNodes;
        }

        const nodesByRestfulApiParent = new Map();
        allNodes.forEach((node) => {
            appendToSetIndex(nodesByRestfulApiParent, node?.data?.restfulapi_parent_node, node.id);
        });

        const nodeIndexById = new Map(allNodes.map((node, index) => [node.id, index]));
        const nodeById = new Map(allNodes.map((node) => [node.id, node]));
        const nextNodes = [...allNodes];

        const updateNodeById = (nodeId, updater) => {
            const nodeIndex = nodeIndexById.get(nodeId);
            if (nodeIndex === undefined) {
                return;
            }

            const updatedNode = updater(nextNodes[nodeIndex]);
            nextNodes[nodeIndex] = updatedNode;
            nodeById.set(nodeId, updatedNode);
        };

        for (const edge of edgesToRemove) {
            const sourceNode = nodeById.get(edge.source);
            const targetNode = nodeById.get(edge.target);

            if (sourceNode?.type === 'application' && targetNode?.type === 'restfulapi') {
                const sourceNodeId = sourceNode.id;
                const targetApiId = targetNode.id;
                const clearResourceBySource = (resources = []) => resources.filter((resource) => resource.id !== sourceNodeId);

                updateNodeById(targetApiId, (node) => ({
                    ...node,
                    data: {
                        ...node?.data,
                        environment_properties: {
                            ...node?.data?.environment_properties,
                            resources: clearResourceBySource(node?.data?.environment_properties?.resources || []),
                        },
                    },
                }));

                const apiChildNodeIds = nodesByRestfulApiParent.get(targetApiId) || new Set();
                apiChildNodeIds.forEach((childNodeId) => {
                    const childNode = nodeById.get(childNodeId);
                    if (!INHERITED_ENV_NODE_TYPES.has(childNode?.type)) {
                        return;
                    }

                    updateNodeById(childNodeId, (node) => ({
                        ...node,
                        data: {
                            ...node?.data,
                            environment_properties: {
                                ...node?.data?.environment_properties,
                                resources: clearResourceBySource(node?.data?.environment_properties?.resources || []),
                            },
                        },
                    }));
                });

                continue;
            }

            if (targetNode?.type === 'data') {
                updateNodeById(targetNode.id, (node) => {
                    const nextErrorText = (node?.data?.error_text || []).filter(
                        (err) => err !== 'Cannot proceed: no connection to a path node.' && err !== 'No RESTful API node connection. Please connect this node to a RESTful API node.'
                    );

                    return {
                        ...node,
                        data: {
                            ...node?.data,
                            connect_with_path: '',
                            general_properties: {
                                ...node?.data?.general_properties,
                                action: node?.data?.general_properties?.action || '',
                            },
                            environment_properties: {
                                ...node?.data?.environment_properties,
                                resources: [],
                            },
                            error_text: nextErrorText,
                        },
                    };
                });
            }
        }

        return nextNodes;
    };

    const validateRemainingGraph = ({ allNodes, allEdges, nodeIdsToRemove, edgeIdsToRemove }) => {
        const remainingNodes = allNodes.filter((node) => !nodeIdsToRemove.has(node.id));
        const remainingNodeIdSet = new Set(remainingNodes.map((node) => node.id));
        const remainingEdges = allEdges.filter((edge) => {
            if (edgeIdsToRemove.has(edge.id)) {
                return false;
            }
            return remainingNodeIdSet.has(edge.source) && remainingNodeIdSet.has(edge.target);
        });

        return {
            nodes: remainingNodes,
            edges: remainingEdges,
        };
    };

    const onDelete = (deletedData) => {
        if (openSimulatorPolicy) {
            return;
        }

        const { edges: deletedEdges = [], nodes: deletedNodes = [] } = deletedData || {};
        const currentNodes = nodesRef.current;
        const currentEdges = edgesRef.current;

        const nodeIdsToRemove = calculateDeletionClosure({
            deletedNodes,
            allNodes: currentNodes,
        });

        const edgeIdsToRemove = calculateEdgesToRemove({
            deletedEdges,
            allNodes: currentNodes,
        });

        const nodesAfterClearInherited = clearInheritedProperties({
            allNodes: currentNodes,
            allEdges: currentEdges,
            edgeIdsToRemove,
        });

        const nextGraph = validateRemainingGraph({
            allNodes: nodesAfterClearInherited,
            allEdges: currentEdges,
            nodeIdsToRemove,
            edgeIdsToRemove,
        });

        if (deletedNodes.length > 0) {
            dispatch(clearSelectedNodeRedux());
        }

        setEdges(nextGraph.edges);
        setNodes(nextGraph.nodes);

        // Keep refs in sync immediately because state/store updates are async.
        edgesRef.current = nextGraph.edges;
        nodesRef.current = nextGraph.nodes;
    };

    const handleSave = async () => {
        localStorage.setItem(POLICY_FILE_UPDATE_STATUS_KEY, 'pending');
        let keepPendingForRefresh = false;

        try {
            const { id, ...oldPolicyFile } = policyFile;
            const now = Date.now();
            const persistedDiagram = createPolicyDomainDiagram(nodesRef.current, edgesRef.current);
            const baselinePolicyFile = getPolicyFileFromStorage({}) || policyFile;
            const baselineDiagram = createPolicyDomainDiagram(
                baselinePolicyFile?.diagram?.nodes || [],
                baselinePolicyFile?.diagram?.edges || []
            );
            const hasStructuralChange = await hasFlowChanged(
                {
                    nodes: baselineDiagram.nodes,
                    edges: baselineDiagram.edges,
                },
                {
                    nodes: persistedDiagram.nodes,
                    edges: persistedDiagram.edges,
                }
            );

            if (!hasStructuralChange) {
                dispatch(setSnackbarMessage({
                    open: true,
                    message: NO_DIAGRAM_CHANGES_MESSAGE,
                    status: 'info',
                }));
                return true;
            }

            let update = {
                ...oldPolicyFile,
                policy_diagram_id: policyFile.policy_diagram_id,
                policy_diagram_name: policyFile.policy_diagram_name,
                creator_id: policyFile.creator_id,
                created_by: policyFile.created_by,
                updated_id: profile.inum,
                updated_by: profile.displayName,
                created_at: policyFile.created_at,
                last_updated: now,
                description: policyFile.description,
                status: "Unverified", // follow diagram updated
                validation: policyFile?.validation || [],
                is_simulated: false,
                diagram: persistedDiagram
            }

            const used_drg_in_diagram = findUsedDRGInPolicy(persistedDiagram.nodes);
            if (used_drg_in_diagram.length > 0) {
                update['drg_id'] = used_drg_in_diagram;
            } else {
                update['drg_id'] = [];
            }

            update['used_subject_attributes'] = findUsedSubjectAttributesInPolicy(persistedDiagram.nodes);
            update = {
                ...update,
                used_clients: findUsedClients(update?.diagram?.nodes || [])
            }

            update = checkDiagramError(update);

            // console.log("Saving policy diagram:", update.diagram);

            const gzippedBody = gzip(JSON.stringify(update)); // บีบอัดข้อมูลด้วย gzip

            const requestOptions = {
                method: "PUT",
                headers: {
                    'Content-Type': 'application/json',
                    'Content-Encoding': 'gzip', // Inform the server the body is gzipped
                },
                body: gzippedBody
                // body: JSON.stringify(update)
            };

            const response = await fetch(`${process.env.REACT_APP_API_URL}/policy-diagram/update-policy/${policyFile?.policy_diagram_id}`, requestOptions);
            if (response.status === 200) {
                keepPendingForRefresh = true;
                setIsUpdatePolicyFile(true);
                const normalizedUpdate = toPolicyDomainFile(update);
                setPolicyFileToStorage(normalizedUpdate, { syncLegacy: true });
                dispatch(setCurrentPolicy(normalizedUpdate));

                // validate policy diagram back-end
                try {
                    const requestPostOptions = {
                        method: "POST",
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(update)
                    };
                    const validate_response = await fetch(process.env.REACT_APP_API_VALIDATION_URL + `/verify/validate-policy?project_id=${project?.project_id}`, requestPostOptions);
                    if (validate_response.status === 200) {
                        // const result = await validate_response.json();
                        // console.log("Validation result:", result);
                    } else {
                        // console.error("Validation API error:", validate_response.status);
                        console.error(validate_response.status);
                        dispatch(setSnackbarMessage({
                            open: true,
                            message: `Error status: ${validate_response.status}`,
                            status: "error",
                        }));
                    }
                } catch (validateError) {
                    console.error(validateError);
                    dispatch(setSnackbarMessage({
                        open: true,
                        message: validateError?.message || "Validation API error",
                        status: "error",
                    }));
                }

                dispatch(setSnackbarMessage({
                    open: true,
                    message: "Policy diagram updated successfully",
                    status: "success",
                }));

                return true;
            }

            console.error(response.status);
            dispatch(setSnackbarMessage({
                open: true,
                message: `Error status: ${response.status}`,
                status: "error",
            }));
            return false;
        } catch (error) {
            console.error(error);
            dispatch(setSnackbarMessage({
                open: true,
                message: error?.message || "Failed to save policy diagram",
                status: "error",
            }));
            return false;
        } finally {
            if (!keepPendingForRefresh) {
                localStorage.setItem(POLICY_FILE_UPDATE_STATUS_KEY, 'idle');
                window.dispatchEvent(new CustomEvent(POLICY_FILE_CHANGED_EVENT));
            }
        }
    };

    handleSaveRef.current = handleSave;

    useEffect(() => {
        const handleToolbarSaveRequest = (event) => {
            const diagramType = event?.detail?.diagramType;
            if (diagramType && diagramType !== DIAGRAM_TYPES.POLICY) {
                return;
            }

            if (openSimulatorPolicy) {
                return;
            }

            handleSaveRef.current?.();
        };

        window.addEventListener(DIAGRAM_TOOLBAR_SAVE_EVENT, handleToolbarSaveRequest);
        return () => window.removeEventListener(DIAGRAM_TOOLBAR_SAVE_EVENT, handleToolbarSaveRequest);
    }, [openSimulatorPolicy]);

    // const handleExportPolicyStructure = async () => {
    //     const user_test = {
    //         displayName: "Bob",
    //         inum: "1234567890",
    //     }

    //     const policy_structure = await convertPoliciesStructure(project, [policyFile], user_test);
    //     // console.log("policy_structure", policy_structure)
    // };

    useEffect(() => {

        const policyId = policyFile?.policy_diagram_id;

        const initializePolicyDiagram = async () => {
            // console.log("initializePolicyDiagram")
            if (!policyFile) {
                return;
            }

            const normalizedPolicyFile = toPolicyDomainFile(policyFile);

            let resolvedSubjectSchema = Array.isArray(subjectSchema) ? subjectSchema : [];

            if (resolvedSubjectSchema.length === 0) {
                const refreshedSubjectSchema = await checkUpdateSubjectSchema(dispatch, resolvedSubjectSchema, { returnData: true });
                if (Array.isArray(refreshedSubjectSchema)) {
                    resolvedSubjectSchema = refreshedSubjectSchema;
                }
            }

            const clientsData = await getListClients();

            // if (clientsData.length === 0) return;

            const flowState = getPolicyViewportFromStorage({
                policyId: normalizedPolicyFile?.policy_diagram_id,
            });
            dispatch(setOpenDrawerRight(false));
            dispatch(setSelectedNodeRedux(undefined));
            const mapDrgValidation = checkDRGValidationFrontend(normalizedPolicyFile?.diagram?.nodes || [], normalizedPolicyFile?.validation, clientsData);
            const mapSubjectConstraintValidation = checkSubjectConstraintValidation(mapDrgValidation, resolvedSubjectSchema);

            setNodes(mapSubjectConstraintValidation);
            setEdges(normalizedPolicyFile?.diagram?.edges || []);

            // // Sync refs immediately because setState is async and other effects may read refs before next render.
            nodesRef.current = mapSubjectConstraintValidation;
            edgesRef.current = normalizedPolicyFile?.diagram?.edges || [];

            setViewport(flowState?.viewport || { x: 0, y: 0, zoom: 1 });
            filterClientsInvalid(mapSubjectConstraintValidation, clientsData);
        };

        if (!policyId) return;

        // console.log("Policy diagram updated");
        initializePolicyDiagram();
        getDRGList();

    }, [policyFile?.policy_diagram_id, policyFile?.last_updated]);

    useEffect(() => {
        if (!openSimulatorPolicy) {
            hasHandledOpenSimulatorRef.current = false;
            return;
        }

        setNodes((prev) =>
            prev.map((node) => ({
                ...node,
                data: {
                    ...node.data,
                    showHandle: false,
                },
            }))
        );
        setEdges((prev) => prev.map((edge) => applyEdgeSelectionStyle(edge, false, false)));
        dispatch(clearSelectedNodeRedux());

        if (!policyFile) return;

        if (hasHandledOpenSimulatorRef.current) {
            return;
        }

        hasHandledOpenSimulatorRef.current = true;

        const latestNodes = nodesRef.current.length > 0 ? nodesRef.current : nodes;
        const latestEdges = edgesRef.current.length > 0 ? edgesRef.current : edges;

        // console.log("Check nodes before open Simulator:", latestNodes);

        if (latestNodes.length === 0) {
            dispatch(setOpenSimulatorPolicy(false));
            dispatch(setSnackbarMessage({
                open: true,
                message: "Cannot open Simulator because the diagram is empty.",
                status: "error",
            }));
            return;
        }

        const updatedFile = {
            ...policyFile,
            diagram: {
                nodes: latestNodes,
                edges: latestEdges
            }
        }

        const result = checkDiagramError(updatedFile);
        if (result?.validation.length > 0) {
            dispatch(setOpenSimulatorPolicy(false));
            dispatch(setSnackbarMessage({
                open: true,
                message: "Cannot open Simulator because the diagram has errors.",
                status: "error",
            }));
        } else {
            const openSimulatorWithLatestPolicy = async () => {
                const isSaved = await handleSave();
                if (!isSaved) {
                    dispatch(setOpenSimulatorPolicy(false));
                    return;
                }

                await handleGetSimulatorProfiles(project, profile, subjectSchema, dispatch);
            };

            void openSimulatorWithLatestPolicy();
        }
    }, [openSimulatorPolicy, policyFile, project, profile, subjectSchema, dispatch, setNodes, setEdges]);

    return (
        <div style={{ height: '100%' }}>
            <PolicyDiagramNodeTypeContext.Provider value={{ onTypeChange: handleTypeChange, isInteractionDisabled: openSimulatorPolicy }}>
                <ReactFlow
                    nodes={nodes}
                    onNodesChange={onNodesChange}
                    edges={edges}
                    onEdgesChange={onEdgesChange}
                    nodeTypes={nodeTypes}
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                    onMoveEnd={saveViewportState}
                    onNodeDoubleClick={onNodeDoubleClick}
                    onNodeClick={onNodeClick}
                    onPaneClick={onPaneClick}
                    onEdgeClick={onEdgeClick}
                    onEdgeMouseEnter={onEdgeMouseEnter}
                    onEdgeMouseLeave={onEdgeMouseLeave}
                    onConnect={onConnect}
                    onConnectStart={onConnectStart}
                    onDelete={onDelete}
                    nodesDraggable={!openSimulatorPolicy}
                    nodesConnectable={!openSimulatorPolicy}
                    elementsSelectable={!openSimulatorPolicy}
                    edgesReconnectable={!openSimulatorPolicy}
                    nodesFocusable={!openSimulatorPolicy}
                    edgesFocusable={!openSimulatorPolicy}
                    connectOnClick={!openSimulatorPolicy}
                    zoomOnScroll={!openSimulatorPolicy}
                    zoomOnPinch={!openSimulatorPolicy}
                    zoomOnDoubleClick={!openSimulatorPolicy}
                    panOnDrag={true}
                    deleteKeyCode={openSimulatorPolicy ? null : ["Delete", "Backspace"]}
                // fitView
                >
                    <Background style={{ backgroundColor: "#fff" }} />
                </ReactFlow>
            </PolicyDiagramNodeTypeContext.Provider>
            <PolicyProperties setNodes={setNodes} nodes={nodes} edges={edges} policyFile={policyFile} setPolicyFile={setPolicyFile} />
            <DialogReuse
                open={openNotifyDialog}
                fullWidth={true}
                maxWidth={"sm"}
                title={"Invalid Clients Found"}
                content={
                    <div className='py-2 overflow-auto'>
                        {clientsInvalid.length > 0 &&
                            <Box>
                                <Typography component={'span'} variant="body1" gutterBottom>
                                    Some clients used in this policy are invalid or unavailable. Please review the related nodes and update their client properties.
                                    {/* {clientsInvalid.map((item, index) => {
                                        return (
                                            <div key={index} className='my-1'>
                                                <Typography component={'span'} variant="body2" color="error">- {item.client_name} ({item.app_type})</Typography>
                                            </div>
                                        )
                                    })} */}
                                </Typography>
                            </Box>
                        }
                    </div>
                }
                actions={
                    <LoadingButton variant="contained" onClick={() => setOpenNotifyDialog(false)}>OK</LoadingButton>
                }
            />
        </div>
    )
}

export default PolicyDiagram